import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getDevotionals = createAsyncThunk(
    'devotionals/getDevotionals',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DEVOTIONALS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotionals retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch devotionals',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch devotionals';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch devotionals';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDevotional = createAsyncThunk(
    'devotionals/getDevotional',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DEVOTIONAL(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional retrieved successfully',
                    devotional: data?.devotional || null,
                    statistics: data?.statistics || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch devotional';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createDevotional = createAsyncThunk(
    'devotionals/createDevotional',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.DEVOTIONAL_CREATE, data);
            const { status, message, data: responseData } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional created successfully',
                    devotional: responseData?.devotional || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to create devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create devotional';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateDevotional = createAsyncThunk(
    'devotionals/updateDevotional',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.DEVOTIONAL_UPDATE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional updated successfully',
                    devotional: responseData?.devotional || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update devotional';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteDevotional = createAsyncThunk(
    'devotionals/deleteDevotional',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.DEVOTIONAL_DELETE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete devotional';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const duplicateDevotional = createAsyncThunk(
    'devotionals/duplicateDevotional',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.DEVOTIONAL_DUPLICATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional duplicated successfully',
                    devotional: data?.devotional || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to duplicate devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to duplicate devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to duplicate devotional';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const toggleDevotional = createAsyncThunk(
    'devotionals/toggleDevotional',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.DEVOTIONAL_TOGGLE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional toggled successfully',
                    devotional: data?.devotional || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to toggle devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to toggle devotional';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendDevotional = createAsyncThunk(
    'devotionals/sendDevotional',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.DEVOTIONAL_SEND(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional sent successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send devotional',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send devotional';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send devotional';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDevotionalSubscribers = createAsyncThunk(
    'devotionals/getSubscribers',
    async ({ id, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DEVOTIONAL_SUBSCRIBERS(id), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Subscribers retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch subscribers',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch subscribers';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch subscribers';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const addDevotionalSubscriber = createAsyncThunk(
    'devotionals/addSubscriber',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.DEVOTIONAL_SUBSCRIBERS(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Subscriber added successfully',
                    subscriber: responseData?.subscriber || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to add subscriber',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to add subscriber';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to add subscriber';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const removeDevotionalSubscriber = createAsyncThunk(
    'devotionals/removeSubscriber',
    async ({ id, subscriberId }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.DEVOTIONAL_SUBSCRIBER_REMOVE(id, subscriberId));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Subscriber removed successfully',
                    subscriberId: subscriberId
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to remove subscriber',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to remove subscriber';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to remove subscriber';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getDevotionalStats = createAsyncThunk(
    'devotionals/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DEVOTIONAL_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Devotional statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch devotional statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch devotional statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch devotional statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportDevotionals = createAsyncThunk(
    'devotionals/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.DEVOTIONAL_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    devotionals: [],
    currentDevotional: null,
    subscribers: [],
    stats: {
        total_devotionals: 0,
        active_devotionals: 0,
        inactive_devotionals: 0,
        total_subscribers: 0,
        sent_this_month: 0,
        opt_in_rate: 0,
        by_frequency: {},
        stats: {
            total: 0,
            active: 0,
            inactive: 0,
            subscribers: 0,
            sent_this_month: 0,
            opt_in_rate: 0
        }
    },
    exportData: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const devotionalSlice = createSlice({
    name: 'devotionals',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearDevotionalState: (state) => {
            state.deotionals = [];
            state.currentDevotional = null;
            state.subscribers = [];
            state.stats = initialState.stats;
            state.exportData = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentDevotional: (state) => {
            state.currentDevotional = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
        clearSubscribers: (state) => {
            state.subscribers = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET DEVOTIONALS =====
            .addCase(getDevotionals.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDevotionals.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.devotionals = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Devotionals retrieved successfully';
                state.apiMessage = payload.message || 'Devotionals retrieved successfully';
                state.error = null;
            })
            .addCase(getDevotionals.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch devotionals';
                state.apiMessage = action.payload?.message || 'Failed to fetch devotionals';
                state.successMessage = null;
                state.devotionals = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET DEVOTIONAL =====
            .addCase(getDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentDevotional = payload.devotional || null;
                state.successMessage = payload.message || 'Devotional retrieved successfully';
                state.apiMessage = payload.message || 'Devotional retrieved successfully';
                state.error = null;
            })
            .addCase(getDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch devotional';
                state.apiMessage = action.payload?.message || 'Failed to fetch devotional';
                state.successMessage = null;
                state.currentDevotional = null;
            })

            // ===== CREATE DEVOTIONAL =====
            .addCase(createDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional created successfully';
                state.apiMessage = payload.message || 'Devotional created successfully';
                state.error = null;
                if (payload.devotional) {
                    state.devotionals = [payload.devotional, ...state.devotionals];
                }
            })
            .addCase(createDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create devotional';
                state.apiMessage = action.payload?.message || 'Failed to create devotional';
                state.successMessage = null;
            })

            // ===== UPDATE DEVOTIONAL =====
            .addCase(updateDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional updated successfully';
                state.apiMessage = payload.message || 'Devotional updated successfully';
                state.error = null;
                if (payload.devotional) {
                    state.devotionals = state.devotionals.map(d => 
                        d.id === payload.devotional.id ? payload.devotional : d
                    );
                    if (state.currentDevotional?.id === payload.devotional.id) {
                        state.currentDevotional = payload.devotional;
                    }
                }
            })
            .addCase(updateDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update devotional';
                state.apiMessage = action.payload?.message || 'Failed to update devotional';
                state.successMessage = null;
            })

            // ===== DELETE DEVOTIONAL =====
            .addCase(deleteDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional deleted successfully';
                state.apiMessage = payload.message || 'Devotional deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.devotionals = state.devotionals.filter(d => d.id !== payload.id);
                    if (state.currentDevotional?.id === payload.id) {
                        state.currentDevotional = null;
                    }
                }
            })
            .addCase(deleteDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete devotional';
                state.apiMessage = action.payload?.message || 'Failed to delete devotional';
                state.successMessage = null;
            })

            // ===== DUPLICATE DEVOTIONAL =====
            .addCase(duplicateDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(duplicateDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional duplicated successfully';
                state.apiMessage = payload.message || 'Devotional duplicated successfully';
                state.error = null;
                if (payload.devotional) {
                    state.devotionals = [payload.devotional, ...state.devotionals];
                }
            })
            .addCase(duplicateDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to duplicate devotional';
                state.apiMessage = action.payload?.message || 'Failed to duplicate devotional';
                state.successMessage = null;
            })

            // ===== TOGGLE DEVOTIONAL =====
            .addCase(toggleDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional toggled successfully';
                state.apiMessage = payload.message || 'Devotional toggled successfully';
                state.error = null;
                if (payload.devotional) {
                    state.devotionals = state.devotionals.map(d => 
                        d.id === payload.devotional.id ? payload.devotional : d
                    );
                    if (state.currentDevotional?.id === payload.devotional.id) {
                        state.currentDevotional = payload.devotional;
                    }
                }
            })
            .addCase(toggleDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle devotional';
                state.apiMessage = action.payload?.message || 'Failed to toggle devotional';
                state.successMessage = null;
            })

            // ===== SEND DEVOTIONAL =====
            .addCase(sendDevotional.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendDevotional.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Devotional sent successfully';
                state.apiMessage = payload.message || 'Devotional sent successfully';
                state.error = null;
                // Update stats
                if (payload.result?.sent && state.stats) {
                    state.stats.sent_this_month = (state.stats.sent_this_month || 0) + payload.result.sent;
                    state.stats.stats.sent_this_month = (state.stats.stats.sent_this_month || 0) + payload.result.sent;
                }
            })
            .addCase(sendDevotional.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send devotional';
                state.apiMessage = action.payload?.message || 'Failed to send devotional';
                state.successMessage = null;
            })

            // ===== GET SUBSCRIBERS =====
            .addCase(getDevotionalSubscribers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDevotionalSubscribers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.subscribers = responseData.data || [];
                state.successMessage = payload.message || 'Subscribers retrieved successfully';
                state.apiMessage = payload.message || 'Subscribers retrieved successfully';
                state.error = null;
            })
            .addCase(getDevotionalSubscribers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch subscribers';
                state.apiMessage = action.payload?.message || 'Failed to fetch subscribers';
                state.successMessage = null;
                state.subscribers = [];
            })

            // ===== ADD SUBSCRIBER =====
            .addCase(addDevotionalSubscriber.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(addDevotionalSubscriber.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Subscriber added successfully';
                state.apiMessage = payload.message || 'Subscriber added successfully';
                state.error = null;
                if (payload.subscriber) {
                    state.subscribers = [payload.subscriber, ...state.subscribers];
                }
            })
            .addCase(addDevotionalSubscriber.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to add subscriber';
                state.apiMessage = action.payload?.message || 'Failed to add subscriber';
                state.successMessage = null;
            })

            // ===== REMOVE SUBSCRIBER =====
            .addCase(removeDevotionalSubscriber.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(removeDevotionalSubscriber.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Subscriber removed successfully';
                state.apiMessage = payload.message || 'Subscriber removed successfully';
                state.error = null;
                if (payload.subscriberId) {
                    state.subscribers = state.subscribers.filter(s => s.id !== payload.subscriberId);
                }
            })
            .addCase(removeDevotionalSubscriber.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to remove subscriber';
                state.apiMessage = action.payload?.message || 'Failed to remove subscriber';
                state.successMessage = null;
            })

            // ===== GET STATS =====
            .addCase(getDevotionalStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDevotionalStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Devotional statistics retrieved successfully';
                state.apiMessage = payload.message || 'Devotional statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getDevotionalStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch devotional statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch devotional statistics';
                state.successMessage = null;
            })

            // ===== EXPORT =====
            .addCase(exportDevotionals.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportDevotionals.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportDevotionals.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearDevotionalState,
    clearCurrentDevotional,
    clearExportData,
    clearSubscribers,
} = devotionalSlice.actions;

export const selectDevotionals = (state) => state.devotionals?.devotionals || [];
export const selectCurrentDevotional = (state) => state.devotionals?.currentDevotional || null;
export const selectDevotionalSubscribers = (state) => state.devotionals?.subscribers || [];
export const selectDevotionalStats = (state) => state.devotionals?.stats || initialState.stats;
export const selectDevotionalPagination = (state) => state.devotionals?.pagination || initialState.pagination;
export const selectDevotionalLoading = (state) => state.devotionals?.isLoading || false;
export const selectDevotionalError = (state) => state.devotionals?.error || null;
export const selectDevotionalSuccess = (state) => state.devotionals?.successMessage || null;
export const selectDevotionalApiMessage = (state) => state.devotionals?.apiMessage || null;
export const selectDevotionalExportData = (state) => state.devotionals?.exportData || null;

export const selectDevotionalStatsSummary = (state) => {
    const stats = selectDevotionalStats(state);
    return {
        totalDevotionals: stats.total_devotionals || 0,
        activeDevotionals: stats.active_devotionals || 0,
        inactiveDevotionals: stats.inactive_devotionals || 0,
        totalSubscribers: stats.total_subscribers || 0,
        sentThisMonth: stats.sent_this_month || 0,
        optInRate: stats.opt_in_rate || 0,
        byFrequency: stats.by_frequency || {},
        stats: stats.stats || {}
    };
};

export default devotionalSlice.reducer;